import { useIntl } from 'react-intl';
import { ChevronLeft, ChevronRight } from 'lucide';

// MUI
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

// common
import Icon from 'common/components/Icon';
import CheckBoxSchema from 'common/SchemaForm/element/checkbox/CheckBoxSchema';

// screen
import { Unit, type RolesToAssign } from 'modules/account/types';
import { CommonRole } from 'modules/account/services/useGetRoles';
import { convertRoles, countCustomers } from 'modules/account/detail/helpers/utils';

interface Props {
  role: CommonRole;
  rolesToAssign: RolesToAssign;
  isCCQL: boolean;
  isCurrentSelected: boolean;
  rolesType: string;
  unitAddress?: Unit;
  /** Trạng thái checked của checkbox (role đã được gán) */
  isChecked?: boolean;
  /** Callback khi toggle checkbox - dùng trên trang detail, không có khi create */
  onToggle?: (checked: boolean) => void;
  viewOnly?: boolean;
}

/**
 * - Component hiển thị thông tin loại vai trò "Tĩnh" của phân loại Vai trò nghiệp vụ hoặc Vai trò quản trị.
 *
 * - Không áp dụng cho loại vai trò là "Động" hoặc phân loại Vai trò hệ thống.
 */
const StaticRoleType = (props: Props) => {
  const {
    role,
    rolesToAssign,
    isCCQL,
    isCurrentSelected,
    rolesType,
    unitAddress,
    isChecked = false,
    onToggle,
    viewOnly = false,
  } = props;

  const { rolesAssignment, customersSource } = rolesToAssign;

  const { formatMessage } = useIntl();
  const roleId = role.id;

  const getCustomersCount = () => {
    if (rolesType === 'adminRoles') {
      return countCustomers(rolesAssignment[convertRoles('forAdminRoles')], roleId);
    }

    if (!isCCQL) {
      // tài khoản đang được tạo là khách hàng
      return countCustomers(rolesAssignment[convertRoles('forClientBusiness')], roleId);
    }

    const found = rolesAssignment.forGovAgencyBusiness.find((item) => item.roleId === roleId);

    if (!found) return 0;
    if ((found.includedClientIds || []).length) {
      return (found.includedClientIds || []).length;
    }

    return customersSource.filter((c) => c.wardName === unitAddress?.ward).length;
  };

  const customerCount = getCustomersCount();

  return (
    <>
      <Typography variant="subtitle1">{role.name}</Typography>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          fontSize: '0.8em',
        }}
      >
        <Typography>{formatMessage({ id: 'account.permissionType' })}:</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography>
            {role.parentId === null
              ? formatMessage({ id: 'account.roles.type.static' })
              : formatMessage({
                  id: 'account.roles.type.dynamic',
                })}
          </Typography>
          {onToggle !== undefined ? (
            <Box sx={{ marginLeft: '8px', width: '24px', height: '24px' }}>
              <CheckBoxSchema
                sx={{ paddingRight: '10px', width: '24px', height: '24px' }}
                disabled={viewOnly}
                checked={isChecked}
                onChange={(_e, checked) => onToggle(checked)}
              />
            </Box>
          ) : (
            <Box sx={{ marginLeft: '8px', width: '24px', height: '24px' }}>
              <Icon icon={isCurrentSelected ? ChevronRight : ChevronLeft} />
            </Box>
          )}
        </Box>
      </Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          fontSize: '0.8em',
        }}
      >
        <Typography>{formatMessage({ id: 'account.appliedUnitCount' })}:</Typography>
        <Box sx={{ marginRight: '34px' }}>
          <Typography
            sx={{
              color: customerCount === 0 ? 'error.main' : 'success.main',
            }}
          >
            {customerCount}
          </Typography>
        </Box>
      </Box>
    </>
  );
};

export default StaticRoleType;
